import PorcelainCatalogLayout from "@/layouts/PorcelainCatalogLayout"
import { Link } from "react-router-dom"
import clsx from "clsx"
import { useGlobalContext } from "@/global/GlobalContext"
import { oneSetPorcelain, porcelainCatalogContents, PorcelainCatalogOrderInstruction, porcelainCatalogProducts } from "@/app/utils/constants"
import ImageSkeleton from "@/components/skeletons/ImageSkeleton"
import plateDisplay from "@/assets/imgs/di-sana/porcelain-catalog/plate-display-2.webp"
import plateDisplay450 from "@/assets/imgs/di-sana/porcelain-catalog/plate-display-2-450.webp"
import plateDisplay600 from "@/assets/imgs/di-sana/porcelain-catalog/plate-display-2-600.webp"
import plateDisplay800 from "@/assets/imgs/di-sana/porcelain-catalog/plate-display-2-800.webp"
import EngraveTitle from "../../components/EngraveTitle"
import GreekLine from "@/components/GreekLine"
import { useEffect, useRef, useState } from "react"
import ProductDisplay from "../../components/PorcelainProductDisplay"
import { ProductDetailText } from "../../components/ProductDetailText"
import OvalShape from "@/assets/imgs/di-sana/porcelain-catalog/oval-shape.webp"
import MainLogo from "@/components/MainLogo"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import BoxImage400 from "@/assets/imgs/di-sana/porcelain-catalog/box-and-packaging-400.webp"
import BoxImage800 from "@/assets/imgs/di-sana/porcelain-catalog/box-and-packaging-800.webp"
import Monkeys from "@/assets/imgs/di-sana/porcelain-catalog/monkeys.webp"
import { formatNumber } from "@/app/utils/helpers"

const PorcelainCatalog = () => {
  const { setLoading, setIsShowBG, setOpenGapura, setIsToggled, setIsPlaying } = useGlobalContext()
  const porcelainCatalogSectionRef = useRef(null);
  const productDisplaySectionRef = useRef(null);
  const oneSetPorcelainSectionRef = useRef(null);
  const howToOrderSectionRef = useRef(null);
  const boxAndPackagingSectionRef = useRef(null);
  const [showBottomGreek, setShowBottomGreek] = useState(false);
  const mediaSm = window.matchMedia("(min-width: 640px)")

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        porcelainCatalogSectionRef,
        productDisplaySectionRef,
        oneSetPorcelainSectionRef,
        howToOrderSectionRef,
        boxAndPackagingSectionRef
      ];

      const inRange = sections.some(ref => {
        if (!ref.current) return false;
        const rect = ref.current.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });

      setShowBottomGreek(inRange);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onClickLogo = () => {
    setLoading("exit")
    setIsShowBG(true)
    setOpenGapura(true)
    setIsToggled(false)
    setIsPlaying(false)
  }

  return (
    <PorcelainCatalogLayout>
      <ImageSkeleton src={plateDisplay450} srcSet={`${plateDisplay450} 450w, ${plateDisplay600} 600w, ${plateDisplay800} 800w, ${plateDisplay} 1200w`} sizes="100vw" custCls={clsx("mt-20 w-full object-cover")} custH="h-[250px] 2sm:h-[300px] md:h-[400px] lg:h-[700px] xl:h-[800px]" alt="Porcelain Catalog Display" />
      <section ref={porcelainCatalogSectionRef} id="porcelainCatalogSection" className="bg-[url('/src/assets/imgs/di-sana/porcelain-catalog/background-blue2.webp')] h-screen md:h-[70vh] lg:h-screen bg-cover bg-bottom">
        <div className="text-center w-full left-0 h-[24vh] py-3 sm:py-4 flex flex-col">
          <Link to="/" onClick={onClickLogo} className="option-logo my-auto mx-auto">
            <MainLogo />
          </Link>
        </div>
        <EngraveTitle isSmaller title="The Loro Blonyo" subTitle="Fine Porcelain Collection" />
        <div className="w-[90%] sm:w-4/5 mx-auto">
          <p className="font-agcasmlt text-charcoal-black text-lg sm:text-xl lg:text-2xl font-light text-center mx-auto mb-6">
            {porcelainCatalogContents.subtitle}
          </p>
          {porcelainCatalogContents.paragraphs.map((p, i) => (
            <p key={i} className="font-agcasmlt text-charcoal-black text-lg sm:text-xl lg:text-2xl font-light text-justify mx-auto mb-6">
              {p}
            </p>
          ))}
        </div>
      </section>
      {/* product display */}
      <section ref={productDisplaySectionRef} id="ProductDisplaySection">
        <div className="w-[90%] sm:w-4/5 mx-auto my-4 sm:my-6 lg:my-8 flex justify-between items-center">
          <p className="text-[#95702e] sm:text-xl md:text-2xl lg:text-3xl"><span className="uppercase font-agcasmlt mr-6">di sini di sana</span><span className="font-sloopScriptPro"> by Rachman & Sons</span></p>
          <p className="text-[#95702e] sm:text-xl md:text-2xl lg:text-3xl uppercase font-agcasmlt">fine porcelain</p>
        </div>
        <div className="py-12 sm:py-16 lg:py-20 w-[90%] md:w-[70%] mx-auto">
          <div className="grid grid-cols-2 gap-4 sm:gap-10 lg:gap-20">
            {porcelainCatalogProducts.map((ph, i) => (
              <ProductDisplay key={i} product={ph} />
            ))}
          </div>
        </div>
      </section>
      {/* one set porcelain section */}
      <section ref={oneSetPorcelainSectionRef} id="OneSetPorcelainSection" className="mb-12">
        <ImageSkeleton isDarker src={plateDisplay450} srcSet={`${plateDisplay450} 450w, ${plateDisplay600} 600w, ${plateDisplay800} 800w, ${plateDisplay} 1200px`} sizes="100vw" custCls={clsx("w-full  object-cover")} custH="h-[250px] 2sm:h-[300px] md:h-[400px] lg:h-[700px] xl:h-[800px]" alt="Porcelain Catalog Display" />
        <GreekLine custCls="relative my-12" />
        <div className="w-[90%] sm:w-4/5 mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-end">
              <h3 className="font-sloopScriptPro text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#95702e] mr-3">
                One Set Porcelain
              </h3>
              <ProductDetailText text={`(${oneSetPorcelain.amount} pieces, as shown)`} custCls="mb-2" />
            </div>
            <ProductDetailText text={`Rp${formatNumber(oneSetPorcelain.price)}`} custCls="mb-2" />
          </div>
          <div>
            {oneSetPorcelain.products.map((p, idx) => (
              <ProductDetailText key={idx} text={p} />
            ))}
          </div>
        </div>
      </section>
      {/* how to order section */}
      <section ref={howToOrderSectionRef} className="py-12 bg-[url('/src/assets/imgs/di-sana/porcelain-catalog/background-blue2.webp')] bg-cover bg-bottom overflow-hidden">
        {/* oval information */}
        <div className="relative mx-auto mb-3 w-[320px] xs:w-[370px] 2sm:w-[400px] sm:w-[500px] md:w-[600px] lg:w-[650px]">
          <ImageSkeleton isDarker src={OvalShape} isDiffSkeletonSize custCls="w-full h-full" custW="w-[400px] sm:w-[500px] md:w-[600px] lg:w-[650px]" custStyle="h-[430px] xs:h-[500px] md:h-[800px] lg:h-[850px] xl:h-[840px]" alt="Oval Shape Decoration" />
          <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full">
            <div>
              <div className="text-center w-full left-0 h-[12vh] 2sm:h-[18vh] sm:h-[20vh] lg:h-[24vh] xl:h-[20vh] py-1 xs:py-2 md:py-4 flex flex-col">
                <Link to="/" onClick={onClickLogo} className="option-logo my-auto mx-auto">
                  <MainLogo />
                </Link>
              </div>
              <h4 className="font-sloopScriptPro text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center font-light text-[#95702e] mb-1 xs:mb-2 md:mb-4">
                How to Order
              </h4>
              <div>
                <InstructionText custCls="w-4/5 md:w-[70%] mx-auto">
                  <span>
                    Each piece of The Fine Porcelain Collection is produced in limited quantities
                    and crafted with care. To place an order, please follow the steps below.
                  </span>
                </InstructionText>
                <div className="flex flex-col gap-y-3 md:gap-y-6 mt-1.5 md:mt-3">
                  {PorcelainCatalogOrderInstruction.map((inst, i) => (
                    <div key={i}>
                      <div className={i == 3 ? "w-2/5 mx-auto" : i == 1 ? "w-[90%] mx-auto" : ""}>
                        <InstructionText custCls={i == 3}>
                          <span>{inst.step}</span>
                          <span className="block">{inst.instruction}</span>
                        </InstructionText>
                        {inst.options && inst.options.length > 0 && (
                          <div className="flex justify-center">
                            <div>
                              <ul className="decoration-none mb-1 md:mb-2 flex gap-2">
                                {inst.options.slice(0, 3).map((opt, idx) => (
                                  <li key={idx} className="flex gap-2">
                                    <InstructionText isSmaller>
                                      {/* TODO: katanya yg ada di attr rel ini buat keamanan, coba tes, kalo iya, terapin ke tag anchor lainnya */}
                                      <a href={opt.link} target="_blank" rel="noopener noreferrer">
                                        <FontAwesomeIcon icon={opt.icon} className="mr-1" />
                                        <span className="hidden 2sm:block">
                                          {opt.detail}
                                        </span>
                                      </a>
                                    </InstructionText>
                                    <div>
                                      {mediaSm.matches && idx < inst.options.length - 2 ?
                                        <div className="border border-separate border-[#9b894d] h-3 sm:h-3.5 md:h-4"></div>
                                        : <div className="border border-separate border-[#9b894d] h-3 sm:h-3.5 md:h-4"></div>}
                                    </div>
                                  </li>
                                ))}
                                <li className="flex 2sm:hidden">
                                  <InstructionText isSmaller>
                                    <a href={inst.options[inst.options.length - 1].link} target="_blank" rel="noopener noreferrer">
                                      <FontAwesomeIcon icon={inst.options[inst.options.length - 1].icon} className="mr-2" />
                                    </a>
                                  </InstructionText>
                                </li>
                              </ul>
                              <ul className="hidden 2sm:block">
                                <li>
                                  <InstructionText isSmaller>
                                    <a href={inst.options[inst.options.length - 1].link} target="_blank" rel="noopener noreferrer">
                                      <FontAwesomeIcon icon={inst.options[inst.options.length - 1].icon} className="mr-2" />
                                      <span>
                                        {inst.options[inst.options.length - 1].detail}
                                      </span>
                                    </a>
                                  </InstructionText>
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <InstructionText custCls="w-[90%] mx-auto">
          <span className="block">
            Notes:
          </span>
          <span className="block">
            All pieces are available in limited quantities. Subtle variations may occur due to the nature of fine porcelain.
          </span>
        </InstructionText>
      </section>
      {/* box & packaging section */}
      <section ref={boxAndPackagingSectionRef} className="py-12 sm:py-16 lg:py-20 bg-[url('/src/assets/imgs/di-sana/porcelain-catalog/background-blue1.webp')] bg-cover bg-center">
        <div className="w-[90%] sm:w-4/5 mx-auto">
          <ImageSkeleton isDarker src={BoxImage800} srcSet={`${BoxImage400} 400w, ${BoxImage800} 800w`} sizes="100vw" alt="Box and Packaging" custCls="w-full" custH="h-[500px] sm:h-[600px] md:h-[700px] lg:h-[800px] object-cover" />
        </div>
        <InstructionText custCls="my-6">
          <span>
            Decorative box & packaging will be sold separately
          </span>
        </InstructionText>
      </section>
      {
        showBottomGreek &&
        <GreekLine custCls="sticky bottom-0" />
      }
      {/* closing section */}
      <section className="bg-[url('/src/assets/imgs/di-sana/porcelain-catalog/background-blue1.webp')] bg-cover bg-bottom">
        <div className="text-center w-full left-0 h-[24vh] py-3 sm:py-4 flex flex-col">
          <Link to="/" onClick={onClickLogo} className="option-logo my-auto mx-auto">
            <MainLogo />
          </Link>
        </div>
        <div className="flex justify-center">
          <div>
            <ImageSkeleton isDarker src={Monkeys} isDiffSkeletonSize custCls="w-full max-w-[300px] sm:max-w-[400px] object-cover" custStyle="h-[200px] sm:h-[250px] md:h-[300px] w-[300px] sm:w-[400px] mb-6" />
            <InstructionText isBigger className="mt-6">
              <span>Made In Indonesia</span>
            </InstructionText>
          </div>
        </div>
      </section>
    </PorcelainCatalogLayout>
  )
}

const InstructionText = ({ children, custCls, isSmaller = false, isBigger = false }) => {
  return (
    <div className={clsx("text-center font-agcasmlt text-[#95702e]", { "text-xs sm:text-sm lg:text-base leading-3 sm:leading-[14px] lg:leading-4": isSmaller, "text-xs xs:text-sm sm:text-base lg:text-lg": !isSmaller && !isBigger, "text-lg sm:text-xl lg:text-2xl": isBigger }, custCls)}>{children}</div>
  )
}

export default PorcelainCatalog